import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import HamburgerMenu from './HamburgerMenu';
import ExitDemoModeButton from './ExitDemoModeButton';
import DemoPill from './DemoPill';
import TrainingPill from '../features/training-mode/components/TrainingPill';
import ActivityCenter from '../features/activity-center/components/ActivityCenter';
import MessagesCenter from '../features/messages/components/MessagesCenter';
import UserProfile from '../features/profile/components/UserProfile';
import GlobalSearch from './GlobalSearch';

import logoUrl from '../assets/kindredly-logo.png';
import butterflyLogoUrl from '../assets/kindredly-butterfly.png';
import { setLastRoute } from '../utils/sessionUtils';
import TabBar from './tabs/TabBar';
import { useTabContext } from '../contexts/TabContext';
import BottomNavBar from './mobile/BottomNavBar';
import MobileTabCounter from './mobile/MobileTabCounter';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useSwipeBack } from '../hooks/useSwipeBack';
import SwipeBackIndicator from './mobile/SwipeBackIndicator';
import { useAuthReady } from '../hooks/useAuthReady';
import { isClientUser, userHasRole } from '../app/router/helpers';
import { useDashboardBootstrap } from '../hooks/useDashboardBootstrap';
import { useMessagesBootstrap } from '../hooks/useMessagesBootstrap';
import TodayBriefingPill from '../features/therapist-panel/components/TodayBriefingPill';
import FeedbackButton from './FeedbackButton';
import BackToTopButton from './mobile/BackToTopButton';
import { useCalendarOptionsContext } from '../contexts/CalendarOptionsContext';
import { useDashboardScrollCollapse } from '../hooks/useDashboardScrollCollapse';

import { MobileCalendarDrawer } from '../features/calendar/components/MobileCalendarDrawer';
import ActiveChartBanner from '../features/client-portal/components/ActiveChartBanner';
import DemoConversionBanner from '../features/demo-login/components/DemoConversionBanner';
import DSTBanner from '../features/notifications/components/DSTBanner';
import DemoConversionPrompt from '../features/demo-login/components/DemoConversionPrompt';
import CharacterModeBanner from '../features/demo-login/components/CharacterModeBanner';
import SubscriptionAlert from '../features/admin-settings/components/SubscriptionAlert';
import PaymentOnboardingBanner from '../features/notifications/components/PaymentOnboardingBanner';
import { useChartUpdateNotifications } from '../hooks/useChartUpdateNotifications';
import { parseAsUtc } from '../utils/dateUtils';
import NotificationsIcon from '@mui/icons-material/Notifications';
const ROUTE_NAME_MAP = {
  dashboard: 'Dashboard',
  'ai-assistant': 'AI Assistant',
  notes: 'Notes',
  patients: 'Clients',
  client: 'Client Portal',
  appointments: 'Appointments',
  messages: 'Messages',
  documents: 'Documents',
  forms: 'Forms',
  health: 'Health Tracking',
  billing: 'Billing',
  calendar: 'Calendar',
  todo: 'To-Do List',
  todos: 'Task Management',
  tasks: 'Task Management',
  vault: 'Library',
  library: 'Library',
  projects: 'Projects',
  canvas: 'Canvas',
  voice: 'Voice Mode',
  'voice-mode': 'Voice Mode',
  'drive-mode': 'Drive Mode',
  settings: 'Settings',
  profile: 'Profile',
  admin: 'Admin',
  staff: 'Staff Management',
  analytics: 'Analytics',
  kpi: 'Provider Analytics',
  'audit-logs': 'Audit Logs',
  'provider-analytics': 'Provider Analytics',
  compliance: 'Compliance',
  chat: 'AI Chat',
  leads: 'Lead Management',
  payments: 'Payments',
  superbills: 'Superbills',
  ar: 'Open A/R',
  revenue: 'Revenue Report',
  gfe: 'Good Faith Estimates',
  insurance: 'Insurance Claims',
  invoices: 'Invoices',
  'payer-payments': 'Payer Payments',
  conversations: 'Conversations',
  notifications: 'Notifications',
  migration: 'Migration Wizard',
  'import-clients': 'Import Clients',
};

function humanizeSegment(seg) {
  if (!seg) return '';

  // Decode URL-encoded characters first
  let decoded = seg;
  try {
    decoded = decodeURIComponent(seg);
  } catch {
    // If decoding fails, use original
    decoded = seg;
  }

  // Check if segment looks like a UUID (8-4-4-4-12 format or similar long hex string)
  const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidPattern.test(decoded)) {
    return 'Details'; // Generic fallback for UUID-based routes
  }
  return isNaN(decoded)
    ? decoded.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ')
    : decoded;
}

function getRouteTitle(path) {
  const normalizedPath = path.split('?')[0];

  if (normalizedPath === '/assistant' || normalizedPath === '/ai-assistant') {
    return 'AI Chat';
  }

  if (normalizedPath === '/projects/canvas' || normalizedPath.startsWith('/projects/canvas/')) {
    return 'AI Project';
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  const last = segments[segments.length - 1] || '';
  return ROUTE_NAME_MAP[last] || humanizeSegment(last);
}



function Layout({ children, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { ready } = useAuthReady();
  const [isScrolled, setIsScrolled] = useState(false);

  const openClaimDialogRef = useRef<(() => void) | null>(null);
  const mainScrollRef = useRef(null);
  const isMobile = useIsMobile();
  const tabContext = useTabContext();
  const calendarOptionsContext = useCalendarOptionsContext();
  
  // Check if we're on a dashboard/calendar page (needs overflow-hidden + scroll-collapse)
  const isCalendarPage = location.pathname === '/dashboard' || location.pathname === '/admin/dashboard' || location.pathname === '/calendar' || location.pathname.startsWith('/calendar/');
  const isCalendarRoute = location.pathname === '/calendar' || location.pathname.startsWith('/calendar/');
  const isAssistantPage = location.pathname === '/ai-assistant';

  // `<main>` owns vertical scrolling for both the calendar and dashboard.
  // MUI's hysteresis drives mobile hide-on-scroll and desktop compact-on-scroll.
  const isCalendarChromeCollapsed = useDashboardScrollCollapse(isCalendarPage, mainScrollRef);
  const isClientPortal = location.pathname === '/client' || location.pathname.startsWith('/client/');
  const isPlatformOwner = userHasRole(user, 'platform_owner');
  
  const { swipeProgress, isSwipingBack } = useSwipeBack({
    onSwipeBack: () => navigate(-1),
    enabled: isMobile,
  });
  
  // Shared messages bootstrap bundle — the unread badge, MessagesCenter
  // dropdown, and AI ConversationContext all read from this single
  // cached query so a fresh page load fires one request instead of three.
  const { data: messagesBootstrap } = useMessagesBootstrap();
  const unreadMessageCount = messagesBootstrap?.unread_count?.care_unread || 0;

  // Clinic branding/logo comes from the shared dashboard bootstrap bundle.
  // The backend returns `branding: null` for non-admins so we treat that as
  // "use the default Kindredly logo".
  const { data: bootstrap } = useDashboardBootstrap();
  const clinicLogoUrl = bootstrap?.branding?.logo_url || null;

  // Sync pending chart update approvals into the bell notification system.
  useChartUpdateNotifications();

  useEffect(() => {
    if (!ready) return;
    if (!user) return;

    try {
      const abandoned = localStorage.getItem('booking:abandoned') === '1';
      const inflight = localStorage.getItem('booking:inflight') === '1';
      if (abandoned || inflight) {
        toast.warn('Previous booking did not finish. Please review your Leads/Calendar.');
        localStorage.removeItem('booking:abandoned');
        localStorage.removeItem('booking:inflight');
      }
    } catch { /* ignore */ }
  }, [ready, user]);
  
  useEffect(() => {
    const path = location.pathname || '';
    const disallowed = new Set(['/', '/login', '/demo-login', '/gated-demo', '/try-kindredly-free', '/name-my-clinic', '/dashboard', '/admin/dashboard']);
    if (!path || disallowed.has(path)) return;

    const title = getRouteTitle(path) || 'Previous Page';

    setLastRoute(path, title);


  }, [location.pathname]);
  

  const { tabs, activeTabId, openTab, closeTab, switchTab, reorderTabs, updateTabRoute, findParentTab, hasRestoredSession, maxTabs } = tabContext;
  
  // Use a ref so this callback never depends on the `tabs` array.
  // Previously, depending on `tabs` meant handleTabClick was recreated on
  // every tab state change, which forced TabBar to re-render on every
  // navigation even when the visual tabs didn't change.
  const tabsSnapshotRef = useRef(tabs);
  useEffect(() => { tabsSnapshotRef.current = tabs; }, [tabs]);

  const handleTabClick = useCallback((tabId) => {
    const tab = tabsSnapshotRef.current.find(t => t.id === tabId);
    if (tab) {
      switchTab(tabId);
      navigate(tab.route);
    }
  }, [switchTab, navigate]);
  
  const handleTabClose = useCallback((tabId) => {
    const nextRoute = closeTab(tabId);
    if (nextRoute) {
      navigate(nextRoute);
    }
  }, [closeTab, navigate]);
  
  const handleNewTab = useCallback(() => {
    navigate('/new-tab');
  }, [navigate]);
  
  const handleTabReorder = useCallback((tabId, newPosition) => {
    reorderTabs(tabId, newPosition);
  }, [reorderTabs]);
  
    useEffect(() => {
      if (!user || !hasRestoredSession || isClientPortal) return;
    
      const path = location.pathname;
      // Preserve query params (e.g. ?mode=guided_today) so guided workflows
      // survive tab switches.  The full route is stored in the tab and used
      // when the user clicks back to this tab.
      const fullRoute = location.search ? `${path}${location.search}` : path;
      const title = getRouteTitle(path) || 'Page';
    
      const skipPaths = ['/', '/login', '/logout', '/demo-login', '/gated-demo', '/try-kindredly-free', '/name-my-clinic', '/register', '/forgot-password', '/reset-password', '/admin'];
      if (skipPaths.includes(path)) return;
    
      // Check if there's a parent tab that should be updated instead of creating a new tab
      // This handles sub-tab navigation within client charts, case details, etc.
      // findParentTab uses a ref internally so it always has current tabs
      const parentTab = findParentTab(path);
      if (parentTab) {
        updateTabRoute(parentTab.id, fullRoute, title);
        switchTab(parentTab.id);
        return;
      }
    
      // No parent tab found, let openTab handle existing tab check or create new tab
      openTab(fullRoute, title, null);
    }, [location.pathname, location.search, user, hasRestoredSession, isClientPortal, openTab, switchTab, findParentTab, updateTabRoute]);
  
  useEffect(() => {
    const scrollEl = mainScrollRef.current;
    if (!scrollEl) return;
    
    const sentinel = document.getElementById('top-sentinel');
    if (!sentinel) return;
    
    const io = new IntersectionObserver(
      (entries) => {
        setIsScrolled(!entries[0].isIntersecting);
      },
      { root: scrollEl, threshold: 1.0 }
    );
    
    io.observe(sentinel);
    return () => io.disconnect();
  }, [mainScrollRef]);

  // ── Global scroll-to-top on route change ──
  // The <main> element is the real scroll container (the window is
  // h-screen overflow-hidden). Reset scroll when navigating pages.
  useEffect(() => {
    mainScrollRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  // ── Global accordion scroll ──
  // When any MUI Accordion header is clicked inside <main>, scroll so the
  // accordion is fully visible after the expand/collapse animation finishes.
  // Uses capture phase so the event is seen even if MUI's ButtonBase
  // stops propagation in the bubble phase.  Scoped to <main> so accordions
  // inside dialogs/drawers don't trigger unexpected page scrolls.
  // Only scrolls when the accordion is being *opened* (not when closing).
  // Uses a longer delay (400ms) to let both the closing and opening
  // animations settle when switching between accordions.
  useEffect(() => {
    const main = mainScrollRef.current;
    if (!main) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!main.contains(target)) return;
      const summary = target.closest('.MuiAccordionSummary-root');
      if (!summary) return;
      const accordion = summary.closest(
        '.MuiAccordion-root',
      ) as HTMLElement | null;
      if (!accordion) return;

      // Check if this accordion is currently collapsed (about to open).
      // MUI adds 'Mui-expanded' before the animation starts, so at click
      // time the class reflects the *previous* state.
      const wasExpanded = accordion.classList.contains('Mui-expanded');
      if (wasExpanded) return; // closing — no scroll needed

      // Wait for both close + open animations to finish, then scroll
      setTimeout(() => {
        accordion.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  // All pages use overflow-y-auto on <main> so the page scrolls naturally
  // and the IntersectionObserver on #top-sentinel fires to collapse the header.
  // On calendar/dashboard pages the sidebar is position:sticky with its own
  // scroll, while the calendar content flows at natural height.
  const mainOverflowClass = isAssistantPage ? 'overflow-hidden' : 'overflow-y-auto';
  
  return (
    <div className={`flex flex-col h-screen overflow-hidden bg-background dark:bg-gray-900 font-sans ${user?.impersonates_user_id ? 'border-4 border-orange-500' : ''}`}>
      {isMobile && <SwipeBackIndicator swipeProgress={swipeProgress} isSwipingBack={isSwipingBack} />}
      {user && <CharacterModeBanner user={user} />}
      
      <header
        className={`app-main-header sticky top-0 z-[50] w-full transition-[height,background-color] duration-200 ease-in-out border-b border-gray-200 dark:border-gray-700 shadow-sm ${
          isScrolled || isCalendarChromeCollapsed
            ? 'h-[var(--header-height-compact)] bg-white/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:backdrop-blur'
            : 'h-[var(--header-height-full)] bg-white dark:bg-gray-800'
        }`}
      >
        {/* Inner wrapper */}
        <div className="h-full">
          {/* Top Bar: Hamburger + Logo on left, tools on right */}
          <div className="flex items-center justify-between px-2 py-2 sm:px-4 h-14">
            <div className="flex items-center space-x-1.5 sm:space-x-3 min-w-0">
              {user && !isClientPortal && <HamburgerMenu onOpenClaimDialog={() => openClaimDialogRef.current?.()} />}
              <Link to={isPlatformOwner ? '/platform-owner' : '/dashboard'} className="flex items-center gap-2 shrink-0">
                {isMobile ? (
                  <img
                    src={butterflyLogoUrl}
                    alt="Kindredly"
                    className="h-8 w-8 cursor-pointer object-contain"
                  />
                ) : (
                  <img 
                    src={import.meta.env.VITE_BRAND_LOGO_URL || logoUrl} 
                    alt="Kindredly" 
                    className="h-8 sm:h-10 w-auto cursor-pointer object-contain" 
                  />
                )}
              </Link>
              {/* Show clinic logo + name next to Kindredly logo after user has claimed their org */}
              {!isMobile && user?.claimed_org && clinicLogoUrl && (
                <>
                  <span className="text-gray-300 dark:text-gray-600 select-none">|</span>
                  <img
                    src={clinicLogoUrl}
                    alt={user?.clinic_name || 'Clinic Logo'}
                    className="h-7 sm:h-8 w-auto object-contain"
                  />
                </>
              )}
              {!isMobile && user?.claimed_org && user?.clinic_name && !clinicLogoUrl && (
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate max-w-[200px]" title={user.clinic_name}>
                  {user.clinic_name}
                </span>
              )}
            </div>

            <div className="flex items-center space-x-0.5 sm:space-x-2 md:space-x-3 shrink-0">
              {/* Show demo pill for shared demo users (pre-claim) AND for claimed users
                   BUT hide it when training_mode is active — TrainingPill takes over.
                   Hide entirely on client portal — clients should never see demo chrome.
                   Hide on mobile too — the demo border + "Viewing as" banner already signal demo mode. */}
              <DemoPill show={!isMobile && !isClientPortal && !isPlatformOwner && !!((user?.demo_mode && !user?.claimed_org) || (user?.claimed_org && !user?.training_mode))} user={user} />
              {/* Show training pill when training sandbox mode is active (post-claim, demo data moved to training) */}
              <TrainingPill show={!isPlatformOwner && !!user?.training_mode && !!user?.claimed_org} user={user} />
              {/* Mobile: magnifying glass icon; Desktop: search bar — responsive handled inside GlobalSearch */}
              {user && !isPlatformOwner && !isClientPortal && <GlobalSearch />}
              {user && isClientPortal && (
                <Link
                  to="/client?tab=notifications"
                  aria-label="Notifications"
                  title="Notifications"
                  className="inline-flex items-center justify-center rounded-full p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <NotificationsIcon fontSize="small" />
                </Link>
              )}
              {user && !isPlatformOwner && !isMobile && <TodayBriefingPill />}
              {/* AI Scribe is intentionally not in the header — it lives in
                  the note/session surfaces. */}
              {user && !isPlatformOwner && !isClientUser(user) && <ActivityCenter />}
              {user && !isPlatformOwner && <MessagesCenter />}
              {user && <UserProfile user={user} />}
            </div>
          </div>

        </div>
        
      </header>
      
      {/* Subscription billing alert — shown to admin-access users when past_due/suspended */}
      {user && !isPlatformOwner && (
        <SubscriptionAlert
          subscriptionStatus={user.subscription_status}
          hasAdminSettingsAccess={user.has_admin_settings_access}
        />
      )}

      {/* Payment onboarding status banner — shown to admins when payment step changes */}
      {user && !isPlatformOwner && (
        <PaymentOnboardingBanner
          hasAdminSettingsAccess={user.has_admin_settings_access}
        />
      )}

      {/* Tab Bar- Chrome-style tabs - Rendered OUTSIDE header to prevent overlap */}
      {user && !isPlatformOwner && !isClientPortal && tabs && tabs.length > 0 && !isMobile && (
        <div className="app-tabbar sticky top-[var(--header-height-full)] z-[49] bg-gray-100 dark:bg-gray-800">
          <TabBar
            tabs={tabs}
            activeTabId={activeTabId}
            onTabClick={handleTabClick}
            onTabClose={handleTabClose}
            onNewTab={handleNewTab}
            onTabReorder={handleTabReorder}
            maxTabs={maxTabs}
          />
        </div>
      )}


      {/* Main Content Area */}
      <main 
        className={`flex flex-col min-h-0 flex-1 ${mainOverflowClass} overflow-x-hidden`}
        ref={mainScrollRef}
        style={{
          paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 4rem)' : '0',
        }}
      >
        <div id="top-sentinel" className="h-px w-px" />
        {user && !isPlatformOwner && <DSTBanner />}
        {/* Hide demo banners on client portal and platform owner */}
        {!isClientPortal && !isPlatformOwner && (
          <>
            <DemoConversionBanner onRegisterOpenClaimDialog={(opener) => { openClaimDialogRef.current = opener; }} />
            <DemoConversionPrompt onOpenClaimDialog={() => openClaimDialogRef.current?.()} />
          </>
        )}
        {isClientPortal && <ActiveChartBanner />}
        {children}
      </main>

      {/* Mobile Bottom Navigation — hidden for platform owner */}
      {!isPlatformOwner && <BottomNavBar user={user} unreadMessageCount={unreadMessageCount} />}
      
      {/* Mobile Tab Counter - Hidden on mobile per user request */}
      {!isMobile && <MobileTabCounter />}

      {/* Demo mode notice */}
      {user?.role === 'demo' && !isClientPortal && <ExitDemoModeButton user={user} />}

      {user && <FeedbackButton />}
      
      {/* Mobile Back to Top button — hidden on calendar where it overlaps the time grid */}
      {!isCalendarRoute && <BackToTopButton />}
      
      {/* Copyright Footer */}
      <div className="hidden sm:block text-center py-2 text-xs text-gray-400 dark:text-gray-600 select-none">
        © 2024–2026 Psy-Tec / Kindredly. All rights reserved.
      </div>

      {/* Version Footer - Hidden dev panel, triple-click to reveal */}
      <DevVersionPanel />
      
      {/* Mobile Calendar Options Drawer - controlled from header three-dot menu */}
      {isMobile && isCalendarPage && calendarOptionsContext?.calendarOptions && (
        <MobileCalendarDrawer
          isOpen={calendarOptionsContext.isDrawerOpen}
          onClose={() => calendarOptionsContext.closeDrawer()}
          viewMode={calendarOptionsContext.calendarOptions.viewMode}
          setViewMode={calendarOptionsContext.calendarOptions.setViewMode}
          hideWeekends={calendarOptionsContext.calendarOptions.hideWeekends}
          setHideWeekends={calendarOptionsContext.calendarOptions.setHideWeekends}
          showInitialsOnly={calendarOptionsContext.calendarOptions.showInitialsOnly}
          setShowInitialsOnly={calendarOptionsContext.calendarOptions.setShowInitialsOnly}
          selectedProviderIds={calendarOptionsContext.calendarOptions.selectedPractitioners}
          onProviderToggle={calendarOptionsContext.calendarOptions.handleProviderToggle}
          setShowPersonalCalendar={undefined}
          setShowResourceLanes={undefined}
          roomViewMode={calendarOptionsContext.calendarOptions.roomViewMode}
          setRoomViewMode={calendarOptionsContext.calendarOptions.setRoomViewMode}
          selectedRooms={calendarOptionsContext.calendarOptions.selectedRooms || []}
          handleRoomToggle={calendarOptionsContext.calendarOptions.handleRoomToggle}
          handleSelectAllRooms={calendarOptionsContext.calendarOptions.handleSelectAllRooms}
          handleDeselectAllRooms={calendarOptionsContext.calendarOptions.handleDeselectAllRooms}
          hideCancelled={calendarOptionsContext.calendarOptions.hideCancelled}
          setHideCancelled={calendarOptionsContext.calendarOptions.setHideCancelled}
        />
      )}
    </div>
  );
}

function DevVersionPanel() {
  const [visible, setVisible] = useState(false);
  const clickCount = useRef(0);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = useCallback(() => {
    clickCount.current += 1;
    if (clickTimer.current) clearTimeout(clickTimer.current);
    if (clickCount.current >= 3) {
      clickCount.current = 0;
      setVisible(v => !v);
    } else {
      clickTimer.current = setTimeout(() => { clickCount.current = 0; }, 500);
    }
  }, []);

  return (
    <div
      className="hidden sm:block fixed bottom-0 right-0 select-none"
      onClick={handleClick}
      style={{ cursor: 'default' }}
    >
      {visible ? (
        <div className="mb-2 mr-2 text-xs text-gray-400 dark:text-gray-600 bg-white dark:bg-gray-800 px-2 py-1 rounded shadow-sm border border-gray-200 dark:border-gray-700">
          <VersionInfo />
        </div>
      ) : (
        <div className="w-8 h-8" />
      )}
    </div>
  );
}

function VersionInfo() {
  // Version info comes from the shared dashboard bootstrap bundle so the
  // footer never fires its own request on Layout remounts.
  const { data: bootstrap } = useDashboardBootstrap();
  const versionData = bootstrap?.version;

  const fallback = {
    commit: import.meta.env.VITE_COMMIT_SHA || null,
    build_timestamp: import.meta.env.VITE_BUILD_TIMESTAMP || null,
  };
  const versionInfo = (versionData || fallback) as {
    commit?: string | null;
    deployed_at?: string;
    build_timestamp?: string | null;
  };
  if (!versionInfo.commit && !versionInfo.build_timestamp) return null;

  const commitShort = (versionInfo.commit && versionInfo.commit !== 'unknown')
    ? versionInfo.commit.substring(0, 7)
    : 'dev';

  let deployDate = 'dev';
  if (versionInfo.deployed_at && versionInfo.deployed_at !== 'unknown') {
    const parsedDate = new Date(versionInfo.deployed_at);
    if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2000) {
      deployDate = parseAsUtc(parsedDate).format('MMM D, YYYY');
    }
  } else if (versionInfo.build_timestamp && versionInfo.build_timestamp !== 'unknown') {
    const timestamp = parseInt(versionInfo.build_timestamp);
    if (!isNaN(timestamp) && timestamp > 0) {
      const parsedDate = new Date(timestamp * 1000);
      if (!isNaN(parsedDate.getTime()) && parsedDate.getFullYear() > 2000) {
        deployDate = parseAsUtc(parsedDate).format('MMM D, YYYY');
      }
    }
  }

  return (
    <span title={`Full commit: ${versionInfo.commit || 'development'}`}>
      v{commitShort} – {deployDate}
    </span>
  );
}

export default memo(Layout);
